import createClient from "openapi-fetch";
import type {
  AccessContextDto,
  AuditEventDto,
  BrandingActivationInput,
  BrandingAggregateDto,
  BrandingDto,
  BrandingValidationDto,
  BrandingVersionCreateInput,
  BrandingVersionDto,
  BrandingVersionPatchInput,
  ExpectedHashMutationInput,
  paths,
  PreviewCreateInput,
  PreviewHandoffDto,
  PreviewTargetDto,
  PreviewTargetUpsertInput,
  ProblemDetails,
  SystemBrandingDto,
  SystemBrandingSummaryDto,
  WorkspaceCreateInput,
  WorkspaceDto,
  WorkspaceMcpTokenDto,
  WorkspaceMcpTokenCreateInput,
  WorkspaceMcpTokenRotateInput,
  IssuedWorkspaceMcpTokenDto,
  WorkspaceRuntimeCredentialDto,
  WorkspaceRuntimeCredentialCreateInput,
  IssuedWorkspaceRuntimeCredentialDto,
  ManagedPreviewSessionDto,
  PreviewSessionCursorDto,
  PreviewSessionListDto,
} from "./generated.ts";

export class ControlPlaneApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

export const controlPlaneOpenApiClient = createClient<paths>();

type OpenApiResult<T> =
  | { readonly data: T; readonly error?: never; readonly response: Response }
  | { readonly data?: never; readonly error: ProblemDetails; readonly response: Response };

function unwrap<T>(result: OpenApiResult<T>): T {
  if (result.error) {
    throw new ControlPlaneApiError(
      result.response.status,
      result.error.detail,
      result.error.title,
      result.error.requestId,
    );
  }
  return result.data;
}

const versionPath = "/api/v1/workspaces/{workspaceId}/branding/versions/{versionId}" as const;

export const controlPlaneApi = {
  async getAccessContext(): Promise<AccessContextDto> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/access-context"));
  },

  async listWorkspaces(): Promise<readonly WorkspaceDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces")).items;
  },

  async createWorkspace(input: WorkspaceCreateInput): Promise<WorkspaceDto> {
    return unwrap(await controlPlaneOpenApiClient.POST("/api/v1/workspaces", { body: input }));
  },

  async getBranding(workspaceId: string): Promise<BrandingAggregateDto> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/branding", {
      params: { path: { workspaceId } },
    }));
  },

  async listVersions(workspaceId: string, includeArchived = true): Promise<readonly BrandingVersionDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/branding/versions", {
      params: { path: { workspaceId }, query: { includeArchived } },
    })).items;
  },

  async createVersion(workspaceId: string, input: BrandingVersionCreateInput): Promise<BrandingVersionDto> {
    return unwrap(await controlPlaneOpenApiClient.POST("/api/v1/workspaces/{workspaceId}/branding/versions", {
      params: { path: { workspaceId } },
      body: input,
    }));
  },

  async getVersion(workspaceId: string, versionId: string): Promise<BrandingVersionDto> {
    return unwrap(await controlPlaneOpenApiClient.GET(versionPath, {
      params: { path: { workspaceId, versionId } },
    }));
  },

  async patchVersion(workspaceId: string, versionId: string, input: BrandingVersionPatchInput): Promise<BrandingVersionDto> {
    return unwrap(await controlPlaneOpenApiClient.PATCH(versionPath, {
      params: { path: { workspaceId, versionId } },
      body: input,
    }));
  },

  async validateVersion(workspaceId: string, versionId: string, expectedDefinitionHash?: string): Promise<BrandingValidationDto> {
    return unwrap(await controlPlaneOpenApiClient.POST(
      "/api/v1/workspaces/{workspaceId}/branding/versions/{versionId}/validate",
      {
        params: { path: { workspaceId, versionId } },
        body: expectedDefinitionHash ? { expectedDefinitionHash } : {},
      },
    ));
  },

  async archiveVersion(workspaceId: string, versionId: string, input: ExpectedHashMutationInput): Promise<BrandingVersionDto> {
    return unwrap(await controlPlaneOpenApiClient.POST(
      "/api/v1/workspaces/{workspaceId}/branding/versions/{versionId}/archive",
      { params: { path: { workspaceId, versionId } }, body: input },
    ));
  },

  async restoreVersion(workspaceId: string, versionId: string, input: ExpectedHashMutationInput): Promise<BrandingVersionDto> {
    return unwrap(await controlPlaneOpenApiClient.POST(
      "/api/v1/workspaces/{workspaceId}/branding/versions/{versionId}/restore",
      { params: { path: { workspaceId, versionId } }, body: input },
    ));
  },

  async publishVersion(workspaceId: string, versionId: string, input: ExpectedHashMutationInput): Promise<BrandingVersionDto> {
    return unwrap(await controlPlaneOpenApiClient.POST(
      "/api/v1/workspaces/{workspaceId}/branding/versions/{versionId}/publish",
      { params: { path: { workspaceId, versionId } }, body: input },
    ));
  },

  async activateVersion(workspaceId: string, versionId: string, input: BrandingActivationInput): Promise<BrandingDto> {
    return unwrap(await controlPlaneOpenApiClient.POST(
      "/api/v1/workspaces/{workspaceId}/branding/versions/{versionId}/activate",
      { params: { path: { workspaceId, versionId } }, body: input },
    ));
  },

  async listSystemBrandings(): Promise<readonly SystemBrandingSummaryDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/system-brandings")).items;
  },

  async getSystemBranding(templateId: string, version: number): Promise<SystemBrandingDto> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/system-brandings/{templateId}/versions/{version}", {
      params: { path: { templateId, version } },
    }));
  },

  async listPreviewTargets(workspaceId: string): Promise<readonly PreviewTargetDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/preview-targets", {
      params: { path: { workspaceId } },
    })).items;
  },

  async upsertPreviewTarget(
    workspaceId: string,
    targetId: string,
    input: PreviewTargetUpsertInput,
  ): Promise<PreviewTargetDto> {
    return unwrap(await controlPlaneOpenApiClient.PUT("/api/v1/workspaces/{workspaceId}/preview-targets/{targetId}", {
      params: { path: { workspaceId, targetId } },
      body: input,
    }));
  },

  async createPreview(workspaceId: string, input: PreviewCreateInput): Promise<PreviewHandoffDto> {
    return unwrap(await controlPlaneOpenApiClient.POST("/api/v1/workspaces/{workspaceId}/preview-sessions", {
      params: { path: { workspaceId } },
      body: input,
    }));
  },

  async listPreviewSessions(
    workspaceId: string,
    input: {
      readonly limit?: number;
      readonly brandingVersionId?: string;
      readonly before?: PreviewSessionCursorDto;
    } = {},
  ): Promise<PreviewSessionListDto> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/preview-sessions", {
      params: {
        path: { workspaceId },
        query: {
          limit: input.limit ?? 25,
          ...(input.brandingVersionId ? { brandingVersionId: input.brandingVersionId } : {}),
          ...(input.before ? { beforeCreatedAt: input.before.createdAt, beforeId: input.before.id } : {}),
        },
      },
    }));
  },

  async revokePreviewSession(
    workspaceId: string,
    sessionId: string,
    idempotencyKey: string,
  ): Promise<ManagedPreviewSessionDto> {
    return unwrap(await controlPlaneOpenApiClient.DELETE(
      "/api/v1/workspaces/{workspaceId}/preview-sessions/{sessionId}",
      {
        params: { path: { workspaceId, sessionId } },
        body: { idempotencyKey },
      },
    ));
  },

  async listAudit(workspaceId: string, limit = 100): Promise<readonly AuditEventDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/audit", {
      params: { path: { workspaceId }, query: { limit } },
    })).items;
  },

  async listOrganizationAudit(organizationId: string, limit = 100): Promise<readonly AuditEventDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/organizations/{organizationId}/audit", {
      params: { path: { organizationId }, query: { limit } },
    })).items;
  },

  async listWorkspaceMcpTokens(workspaceId: string): Promise<readonly WorkspaceMcpTokenDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/mcp-tokens", {
      params: { path: { workspaceId } },
    })).items;
  },

  async createWorkspaceMcpToken(workspaceId: string, input: WorkspaceMcpTokenCreateInput): Promise<IssuedWorkspaceMcpTokenDto> {
    return unwrap(await controlPlaneOpenApiClient.POST("/api/v1/workspaces/{workspaceId}/mcp-tokens", {
      params: { path: { workspaceId } },
      body: input,
    }));
  },

  async rotateWorkspaceMcpToken(workspaceId: string, tokenId: string, input: WorkspaceMcpTokenRotateInput): Promise<IssuedWorkspaceMcpTokenDto> {
    return unwrap(await controlPlaneOpenApiClient.POST("/api/v1/workspaces/{workspaceId}/mcp-tokens/{tokenId}/rotate", {
      params: { path: { workspaceId, tokenId } },
      body: input,
    }));
  },

  async revokeWorkspaceMcpToken(workspaceId: string, tokenId: string, idempotencyKey: string): Promise<WorkspaceMcpTokenDto> {
    return unwrap(await controlPlaneOpenApiClient.DELETE("/api/v1/workspaces/{workspaceId}/mcp-tokens/{tokenId}", {
      params: { path: { workspaceId, tokenId } },
      body: { idempotencyKey },
    }));
  },

  async listWorkspaceRuntimeCredentials(workspaceId: string): Promise<readonly WorkspaceRuntimeCredentialDto[]> {
    return unwrap(await controlPlaneOpenApiClient.GET("/api/v1/workspaces/{workspaceId}/runtime-credentials", {
      params: { path: { workspaceId } },
    })).items;
  },

  async createWorkspaceRuntimeCredential(
    workspaceId: string,
    input: WorkspaceRuntimeCredentialCreateInput,
  ): Promise<IssuedWorkspaceRuntimeCredentialDto> {
    return unwrap(await controlPlaneOpenApiClient.POST("/api/v1/workspaces/{workspaceId}/runtime-credentials", {
      params: { path: { workspaceId } },
      body: input,
    }));
  },

  async revokeWorkspaceRuntimeCredential(workspaceId: string, credentialId: string, idempotencyKey: string): Promise<WorkspaceRuntimeCredentialDto> {
    return unwrap(await controlPlaneOpenApiClient.DELETE("/api/v1/workspaces/{workspaceId}/runtime-credentials/{credentialId}", {
      params: { path: { workspaceId, credentialId } },
      body: { idempotencyKey },
    }));
  },
};
